import { ListParams } from "../models/listParams";
import { OrganisatiesWebService } from "../webservices/organisaties.web.service";
import { retryWithAuth } from "./utils/retryWithAuth";
import { BaseService } from "./base.service";
import { Organisatie } from "../models/organisatie/organisatie";
import { LayerPublic } from "../models/maps/layerPublic";
import { PagedResult } from "../models/pagedResult";

export class OrganisatiesService extends BaseService {
  
  // Private static instance of the class (singleton)
  private static instance: OrganisatiesService;
  private organisatiesCache: Organisatie[] | null = null;
  private organisatiesCachePromise: Promise<Organisatie[]> | null = null;

  // Private constructor to prevent direct instantiation
  private constructor() {
    super();
  }

  // Method to return the singleton instance
  public static getInstance(): OrganisatiesService {
    if (!OrganisatiesService.instance) {
      OrganisatiesService.instance = new OrganisatiesService();
    }
    return OrganisatiesService.instance;
  }

  public async getOrganisaties(
    params: ListParams,
    isZonderGebied: boolean = false,
    options?: { redirectOnForbidden?: boolean }
  ): Promise<any> {
    const organisatieWebService = new OrganisatiesWebService();

    return retryWithAuth(
      () => organisatieWebService.getOrganisaties(params, isZonderGebied),
      2,
      { redirectOnForbidden: options?.redirectOnForbidden ?? true }
    );
  }

  public async getAllOrganisatiesCached(forceRefresh: boolean = false, isZonderGebied: boolean = false): Promise<Organisatie[]> {
    if (!forceRefresh && this.organisatiesCache) {
      return this.organisatiesCache;
    }

    if (!forceRefresh && this.organisatiesCachePromise) {
      return this.organisatiesCachePromise;
    }

    this.organisatiesCachePromise = this.fetchAllOrganisaties(isZonderGebied);

    try {
      this.organisatiesCache = await this.organisatiesCachePromise;
      return this.organisatiesCache;
    }
    catch (error: any) {
      if (this.isForbiddenError(error)) {
        console.warn('[OrganisatiesService] 403 ontvangen voor de huidige rol bij getAllOrganisatiesCached; fallback naar lege lijst.');
        this.organisatiesCache = [];
        return this.organisatiesCache;
      }

      throw error;
    }
    finally {
      this.organisatiesCachePromise = null;
    }
  }

  public clearOrganisatiesCache(): void {
    this.organisatiesCache = null;
    this.organisatiesCachePromise = null;
  }

  public getOrganisatieNaam(id: string): string {
    if (!this.organisatiesCache) return id;
    const org = this.organisatiesCache.find((o) => o.id === id);
    return org?.displayName || org?.name || id;
  }

  private async fetchAllOrganisaties(isZonderGebied: boolean = false): Promise<Organisatie[]> {
    const data = await this.fetchAllPages<Organisatie>(
      (params) => this.getOrganisaties(params, isZonderGebied, { redirectOnForbidden: false }),
      { offset: 0, limit: 200 }
    );

    return data.items;
  }

  public async updateOrganisatie(organisatie: Organisatie, isZonderGebied: boolean = false): Promise<any> {
    const organisatieWebService = new OrganisatiesWebService();

    return retryWithAuth(() => organisatieWebService.updateOrganisatie(organisatie, isZonderGebied), 2);
  }

  public async updateOrganisatieDisplayName(organisatie: Organisatie, isZonderGebied: boolean = false): Promise<any> {
    const organisatieWebService = new OrganisatiesWebService();

    return retryWithAuth(() => organisatieWebService.updateOrganisatieDisplayName(organisatie, isZonderGebied), 2);
  }

  public async deleteOrganisatie(id: string, isZonderGebied: boolean = false): Promise<any> {
    const organisatieWebService = new OrganisatiesWebService();

    return retryWithAuth(() => organisatieWebService.deleteOrganisatie(id, isZonderGebied), 2);
  }

  // Public Layers (Admin)

  public getPublicLayer(id: string): Promise<LayerPublic> {
    const organisatieWebService = new OrganisatiesWebService();
    return retryWithAuth<LayerPublic>(() => organisatieWebService.getLayer(id), 2);
  }

  public getPublicLayersOLD(): Promise<LayerPublic[]> {
    const organisatieWebService = new OrganisatiesWebService();
    return retryWithAuth<LayerPublic[]>(() => organisatieWebService.getLayersOLD(), 2);
  }

  public getPublicLayers(params: ListParams): Promise<PagedResult<LayerPublic>> {
    const organisatieWebService = new OrganisatiesWebService();
    return retryWithAuth<PagedResult<LayerPublic>>(() => organisatieWebService.getLayers(params), 2);
  }

  public updatePublicMap(layer: LayerPublic): Promise<any> {
    const organisatieWebService = new OrganisatiesWebService();
    return retryWithAuth(() => organisatieWebService.putLayer(layer), 2);
  }

  public addPublicMap(layer: LayerPublic): Promise<any> {
    const organisatieWebService = new OrganisatiesWebService();
    return retryWithAuth(() => organisatieWebService.postLayer(layer), 2);
  }

  public deletePublicMap(id: string): Promise<any> {
    const organisatieWebService = new OrganisatiesWebService();
    return retryWithAuth(() => organisatieWebService.deleteLayer(id), 2);
  }

  public async getAllOrganisaties(isZonderGebied: boolean = false): Promise<Organisatie[]> {
    const data = await this.fetchAllPages<Organisatie>(
      (params) => this.getOrganisaties(params, isZonderGebied),
      { offset: 0, limit: 100 }
    );

    return data.items;
  }
};

// Export an instance of the OrganisatieService class
export const organisatieService = OrganisatiesService.getInstance();